import { useState, useRef, useEffect, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Activity,
  Beaker,
  Bot,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  Code2,
  ExternalLink,
  FolderOpen,
  GitBranch,
  GitMerge,
  KeyRound,
  Lightbulb,
  ListChecks,
  LogOut,
  Menu,
  MessageSquare,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Table2,
  User,
  UserCircle,
  Users,
  Wrench,
  X,
} from 'lucide-react'
import { useAuth } from './auth/AuthContext'
import { ClassificationShellBanner } from './ClassificationShellBanner'
import { ThemeToggle } from './ThemeToggle'
import { DefconLogo } from '../components/brand/DefconLogo'
import { useActiveModule } from '../hooks/useActiveModule'
import { useModuleCapabilities } from '../hooks/useModuleCapabilities'
import { BuildBadge } from './BuildBadge'
import { resolveKeycloakAdminUrl } from './keycloakAdminLink'

interface ShellProps {
  children: ReactNode
}

interface NavItem {
  path: string
  label: string
  icon: typeof Beaker
  externalUrl?: string
  /** Use exact match for active state (e.g., Dashboard vs /modules/:name/*) */
  exact?: boolean
  children?: NavItem[]
}

/**
 * Application shell with persistent sidebar navigation.
 * Layout matches the DEFCON AI Figma Design System Playground.
 *
 * Navigation hierarchy reflects the Workspace ↔ Environment ↔ Workbench model
 * (ADR-0069 v2 ontology — module replaces workflow): module-scoped tools
 * (Workbench, Workspace, AI, Code) are nested under the active module in the
 * sidebar, driven by the URL pattern /modules/:name/*.
 */
export function Shell({ children }: ShellProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const activeModule = useActiveModule()
  const { capabilities } = useModuleCapabilities(activeModule?.name)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  /**
   * User-driven expand/collapse overrides keyed by parent path. When a key
   * is undefined, expansion auto-derives from "is any child active?". When
   * the user clicks a parent, we set true/false here and that wins until
   * they click again.
   */
  const [expandOverride, setExpandOverride] = useState<Record<string, boolean>>({})
  const userMenuRef = useRef<HTMLDivElement>(null)

  const isAdmin = user?.roles.some((r) => r.toLowerCase() === 'admin')
  const hasAnalystRole = user?.roles.some(
    (r) => r.toLowerCase() === 'analyst' || r.toLowerCase() === 'admin',
  )
  // Cross-Domain Release Review is Admin OR SecurityLead (server-side
  // ReleaseReviewAccess is the boundary). A SecurityLead who is not an Admin
  // still needs the link, so it surfaces top-level for the non-admin reviewer.
  const isSecurityLead = user?.roles.some((r) => r.toLowerCase() === 'securitylead')
  const keycloakAdminUrl = resolveKeycloakAdminUrl()

  // Close user menu on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close mobile sidebar on route change
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  // ── Route matching ────────────────────────────────────────────────────

  function isActive(path: string, exact = false): boolean {
    if (path === '/') return location.pathname === '/'
    if (exact) return location.pathname === path
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  function anyChildActive(item: NavItem): boolean {
    return !!item.children?.some((c) => isActive(c.path, c.exact) || anyChildActive(c))
  }

  // ── Nav item renderer ─────────────────────────────────────────────────

  function NavLink({ item, indent = false }: { item: NavItem; indent?: boolean }) {
    const active = isActive(item.path, item.exact)
    const Icon = item.icon
    const hasChildren = !!item.children?.length
    const isExternal = !!item.externalUrl
    const override = expandOverride[item.path]
    const isExpanded = hasChildren && (override ?? anyChildActive(item))
    const linkClassName = `
      flex items-center justify-between gap-1.5 w-full
      ${indent ? 'pl-8 pr-2' : 'px-2'} py-1.5 rounded-xl
      text-base font-medium transition-colors no-underline
      ${
        active && !hasChildren
          ? 'bg-surface-active text-accent'
          : 'text-content hover:bg-surface-tertiary'
      }
    `

    return (
      <div>
        <div className="flex items-center">
          {isExternal ? (
            <a
              href={item.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setSidebarOpen(false)}
              className={linkClassName}
              aria-label={`${item.label} (opens in a new tab)`}
            >
              <span className="flex items-center gap-1.5 min-w-0">
                <Icon className="w-5 h-5 flex-shrink-0 text-content-secondary" />
                <span className="truncate">{item.label}</span>
              </span>
              <ExternalLink
                className="w-4 h-4 flex-shrink-0 text-content-tertiary"
                aria-hidden="true"
              />
            </a>
          ) : (
            <Link
              to={hasChildren ? '#' : item.path}
              onClick={
                hasChildren
                  ? (e) => {
                      e.preventDefault()
                      setExpandOverride((prev) => ({ ...prev, [item.path]: !isExpanded }))
                    }
                  : undefined
              }
              className={linkClassName}
            >
              <span className="flex items-center gap-1.5 min-w-0">
                <Icon
                  className={`w-5 h-5 flex-shrink-0 ${active && !hasChildren ? 'text-accent' : 'text-content-secondary'}`}
                />
                <span className="truncate">{item.label}</span>
              </span>
              {hasChildren && (
                <span className="text-content-tertiary">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </span>
              )}
            </Link>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div className="mt-1 flex flex-col gap-0.5">
            {item.children?.map((child) => (
              <NavLink key={child.path} item={child} indent />
            ))}
          </div>
        )}
      </div>
    )
  }

  // ── Build module-scoped nav items (ADR-0069 v2 + ADR-0068) ─────────────

  function buildModuleNav(): NavItem[] {
    if (!activeModule) return []
    const moduleName = activeModule.name

    // ADR-0069 D9 / F318 — client-side nav gate for Code Assistant.
    // The actual security boundary is the server-side
    // WorkbenchCodeAssistantAccess policy (F313/F315); this check
    // only avoids dead links for users that lack the grant. Admin
    // always sees the entry so admins can navigate without first
    // self-granting.
    const userGrants = user?.grants ?? []
    const hasCodeAssistantGrant =
      isAdmin ||
      userGrants.includes(`modules/${moduleName}:workbench:code-assistant`) ||
      userGrants.includes(`modules/${moduleName}:admin`)

    // ADR-0068: nav is a projection of the module's capability block.
    // Capability source of truth = module.yaml (CAPABLE-OF) ∪ Helm values
    // (ENABLED-FOR-THIS-DEPLOYMENT). Surface lives under /api/discovery.
    // Customers (no analyst role) see Portal + AI Assistant; analysts
    // additionally see Workbench. Module Status and a standalone API
    // page were intentionally dropped (VP Eng decision, 2026-05-18):
    // Module Status served no audience, and the external REST API is
    // a deployment capability (capabilities.api.enabled) rather than a
    // UI surface — integrators consume the module's /docs OpenAPI page
    // directly.
    const items: NavItem[] = []

    if (capabilities.portal.enabled) {
      items.push({ path: `/modules/${moduleName}/portal`, label: 'Portal', icon: Beaker })
    }

    // AI Assistant — universal-visibility chatbot rendered in Portal.
    // ADR-0063 amendment (2026-05-18): per-module capability flag.
    // Not analyst-gated — every authenticated user with module access
    // sees the entry when the customer's deployment enables it.
    // (Server-side AgentChatAccess remains the security boundary.)
    if (capabilities.ai_assistant.enabled) {
      items.push({ path: `/modules/${moduleName}/ai`, label: 'AI Assistant', icon: Bot })
    }

    // Workbench — analyst tooling fleet (ADR-0062).
    // ADR-0069 §2a correction: Pipeline + Dashboards live under
    // Workbench. Customers don't author pipelines; they consume Portal
    // outputs. Workbench gate: capabilities.workbench.enabled AND user
    // has analyst role (workbench is platform-restricted by role even
    // when capability is on for the module — defense in depth).
    //
    // dbt does NOT get its own nav child (VP Eng 2026-05-18): real dbt
    // work happens in Jupyter / CLI / dbt-docs, not in a shallow in-app
    // tab. The dbt facets (lineage, run status, model files) fold into
    // Pipeline as sections. capabilities.workbench.dbt remains as a
    // deployment flag — it controls whether the dbt project deploys
    // and whether Pipeline shows dbt sections, not a nav entry.
    if (capabilities.workbench.enabled && hasAnalystRole) {
      const wb = capabilities.workbench
      const children: NavItem[] = [
        {
          path: `/modules/${moduleName}/workbench`,
          label: 'Tools',
          icon: Wrench,
          exact: true,
        },
      ]
      if (wb.dashboards) {
        children.push({
          path: `/modules/${moduleName}/workbench/dashboards`,
          label: 'Dashboards',
          icon: GitBranch,
        })
      }
      if (wb.code_assistant && hasCodeAssistantGrant) {
        children.push({
          path: `/modules/${moduleName}/code-assistant`,
          label: 'Code Assistant',
          icon: Code2,
        })
      }
      items.push({
        path: `/modules/${moduleName}/workbench`,
        label: 'Workbench',
        icon: Wrench,
        children,
      })
    }

    items.push({ path: `/modules/${moduleName}/workspace`, label: 'Workspace', icon: FolderOpen })
    items.push({ path: `/modules/${moduleName}/ideas`, label: 'Ideas', icon: MessageSquare })

    return items
  }

  // ── Sidebar content (shared between desktop and mobile) ───────────────

  function SidebarContent() {
    const moduleNav = buildModuleNav()

    return (
      <div className="flex flex-col h-full">
        {/* Header — logo left-aligned */}
        <div className="flex items-center pb-5 border-b border-border w-full">
          <Link to="/" className="no-underline hover:opacity-80 transition-opacity">
            <DefconLogo size="xs" className="text-content" />
          </Link>
        </div>

        {/* Scrollable nav area — no section labels, dividers only (per Figma Playground) */}
        <nav className="flex-1 overflow-y-auto flex flex-col gap-2 pt-5">
          {/* Primary nav */}
          <div className="flex flex-col gap-1">
            <NavLink item={{ path: '/', label: 'Modules', icon: Beaker }} />
          </div>

          {/* Module tools — visible when in module context (ADR-0069) */}
          {activeModule && (
            <>
              <div className="border-t border-border" />
              <div className="flex flex-col gap-1">
                {moduleNav.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}
              </div>
            </>
          )}

          {/* Platform tools (role-gated) */}
          <div className="border-t border-border" />
          <div className="flex flex-col gap-1">
            {hasAnalystRole && (
              <NavLink item={{ path: '/ideas', label: 'Platform Ideas', icon: Lightbulb }} />
            )}
            {hasAnalystRole && (
              <NavLink item={{ path: '/observability', label: 'Observability', icon: Activity }} />
            )}
            {isAdmin && (
              <NavLink
                item={{
                  path: '/admin',
                  label: 'Admin Panel',
                  icon: Shield,
                  children: [
                    { path: '/admin/users', label: 'Users', icon: Users },
                    { path: '/admin/roles', label: 'Roles', icon: ClipboardList },
                    { path: '/admin/security', label: 'Security', icon: ShieldAlert },
                    { path: '/admin/releases', label: 'Release Review', icon: GitMerge },
                    ...(keycloakAdminUrl
                      ? [
                          {
                            path: 'keycloak-admin',
                            label: 'Keycloak',
                            icon: KeyRound,
                            externalUrl: keycloakAdminUrl,
                          },
                        ]
                      : []),
                    // Audit demoted from top-level (VP Eng 2026-05-18) — it's
                    // an admin-only specialized surface used rarely (forensics,
                    // AO review). Bulk audit evidence ships from an automated
                    // AWS pipeline (S3 Object Lock + Audit Manager), not from
                    // the UI; the UI handles drill-in + on-demand integrity
                    // verification only. Routes stay at /audit/*; AdminLayout
                    // already renders the breadcrumb for both surfaces.
                    // /audit/docs intentionally NOT in this sidebar.
                    // Audit reference material is a *help* surface, not
                    // an operational peer of Events/Integrity. The
                    // operational pages render a contextual "Reference"
                    // link in their hero so an auditor can pull up the
                    // manual without leaving the data they're reviewing.
                    {
                      path: '/audit',
                      label: 'Audit',
                      icon: ListChecks,
                      children: [
                        { path: '/audit/events', label: 'Events', icon: Activity },
                        { path: '/audit/integrity', label: 'Integrity', icon: ShieldCheck },
                        // Table Explorer — operational peer (browses the live
                        // store). /audit/docs + /audit/compare stay OUT of the
                        // sidebar: reference surfaces reached via the hero
                        // links in AdminLayout, not nav peers.
                        { path: '/audit/tables', label: 'Tables', icon: Table2 },
                      ],
                    },
                  ],
                }}
              />
            )}
            {/* SecurityLead (without Admin) still needs the release-review queue. */}
            {!isAdmin && isSecurityLead && (
              <NavLink
                item={{ path: '/admin/releases', label: 'Release Review', icon: GitMerge }}
              />
            )}
          </div>
        </nav>

        {/* User section */}
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 min-w-0" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-label="User menu"
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
                className="flex items-center gap-2.5 py-1 px-1 rounded-lg transition-colors cursor-pointer bg-transparent border-none hover:bg-surface-tertiary w-full"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-surface-tertiary border border-border flex-shrink-0">
                  <User className="w-4 h-4 text-content-secondary" />
                </div>
                <div className="flex flex-col items-start min-w-0">
                  <span className="text-sm font-medium text-content truncate max-w-[140px]">
                    {user?.displayName ?? 'User'}
                  </span>
                  <span className="text-xs text-content-tertiary truncate max-w-[140px]">
                    {user?.email}
                  </span>
                </div>
                {userMenuOpen ? (
                  <ChevronUp className="w-3.5 h-3.5 text-content-tertiary flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-content-tertiary flex-shrink-0" />
                )}
              </button>

              {/* User dropdown */}
              {userMenuOpen && (
                <div className="absolute bottom-full left-0 mb-2 w-56 rounded-xl py-2 z-50 bg-surface-elevated border border-border shadow-theme-xl">
                  <div className="px-3 py-2 border-b border-border-secondary">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary mb-1.5">
                      Roles
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {user?.roles.map((role) => (
                        <span
                          key={role}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-accent bg-accent/10"
                        >
                          <Shield className="w-3 h-3" />
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setUserMenuOpen(false)
                        navigate('/profile')
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-content hover:bg-surface-secondary transition-colors cursor-pointer bg-transparent border-none"
                    >
                      <UserCircle className="w-4 h-4" />
                      Profile
                    </button>
                    {/*
                      ADR-0076: Backstage developer portal. Served behind the
                      gateway at /workbench/backstage (a proxied route, NOT a
                      React Router route), so this is a raw anchor opening in a
                      new tab. Role-gated to Analyst/Admin to match the
                      gateway's AnalystOrAdmin policy (cosmetic gate only).
                    */}
                    {hasAnalystRole && (
                      <a
                        href="/workbench/backstage/"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-content hover:bg-surface-secondary transition-colors no-underline"
                      >
                        <Code2 className="w-4 h-4" />
                        Dev Portal
                      </a>
                    )}
                    <button
                      onClick={() => {
                        setUserMenuOpen(false)
                        logout()
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-error-soft transition-colors cursor-pointer bg-transparent border-none"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
            <ThemeToggle />
          </div>

          {/* Separator + centered build badge — quiet, recedes */}
          <div className="mt-3 pt-3 border-t border-border flex justify-center">
            <BuildBadge className="border-border bg-surface px-2.5 py-1 text-content-tertiary" />
          </div>
        </div>
      </div>
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col min-h-screen bg-surface">
      <ClassificationShellBanner />

      <div className="flex flex-1">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-72 flex-shrink-0 bg-surface-secondary border-r border-border p-5">
          <SidebarContent />
        </aside>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <>
            <div
              role="presentation"
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setSidebarOpen(false)
              }}
            />
            <aside className="fixed inset-y-0 left-0 z-50 w-72 bg-surface-secondary border-r border-border p-5 lg:hidden">
              <div className="flex justify-end mb-2">
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1 rounded-lg hover:bg-surface-tertiary text-content-secondary bg-transparent border-none cursor-pointer"
                  aria-label="Close sidebar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <SidebarContent />
            </aside>
          </>
        )}

        {/* Content area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">
          {/* Mobile header */}
          <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-surface-overlay border-b border-border backdrop-blur-lg">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-1.5 rounded-lg hover:bg-surface-tertiary text-content-secondary bg-transparent border-none cursor-pointer"
              aria-label="Open navigation"
            >
              <Menu className="w-5 h-5" />
            </button>
            <DefconLogo size="xs" className="text-content" />
          </header>

          {/* Main content — no max-width, sidebar is the structural anchor */}
          <main className="flex-1 px-10 py-8">{children}</main>
        </div>
      </div>

      <ClassificationShellBanner />
    </div>
  )
}
